import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { FileText, Shield, Heart, ArrowRight, CheckCircle } from "lucide-react";

const AdvanceCarePlanning = () => {
  return (
    <>
      <Helmet>
        <title>Advance Care Planning Columbia, MO | End-of-Life Doula | Serenity Passage</title>
        <meta
          name="description"
          content="Advance care planning services in Columbia, Missouri. Guidance for Advance Directives, Healthcare Power of Attorney, and Living Will preparation. End-of-life doula support in Mid-Missouri."
        />
        <meta
          name="keywords"
          content="advance care planning Columbia MO, advance directive Mid-Missouri, healthcare power of attorney Columbia Missouri, living will, end-of-life planning"
        />
        <link rel="canonical" href="https://www.serenitypassage.net/advance-care-planning" />
      </Helmet>

      {/* Hero Banner */}
      <section className="bg-gradient-to-b from-rose-100 to-rose-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-serif text-gray-800 text-center mb-4">
            Advance Care Planning
          </h1>
          <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto">
            Ensuring your voice and values guide your care when it matters most
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 text-lg leading-relaxed mb-8">
              Advance care planning is a thoughtful process of reflecting on your values, beliefs, and preferences for future healthcare decisions. I provide gentle guidance to help you complete important documents that ensure your wishes are known and honored.
            </p>

            <h2 className="text-2xl font-serif text-gray-800 mb-6 flex items-center gap-3">
              <FileText className="w-7 h-7 text-rose-600" />
              Documents I Help You Complete
            </h2>

            <div className="space-y-6 mb-12">
              <div className="bg-rose-50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-rose-600" />
                  Advance Directive
                </h3>
                <p className="text-gray-600">
                  A document that outlines your preferences for medical treatment if you become unable to communicate. This ensures healthcare providers understand your wishes.
                </p>
              </div>

              <div className="bg-rose-50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-rose-600" />
                  Healthcare Power of Attorney
                </h3>
                <p className="text-gray-600">
                  Designates a trusted person to make medical decisions on your behalf when you cannot. I help you choose the right person and communicate your values to them.
                </p>
              </div>

              <div className="bg-rose-50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-rose-600" />
                  Living Will
                </h3>
                <p className="text-gray-600">
                  Specifies which life-prolonging treatments you do or do not want. This brings clarity and peace of mind to both you and your loved ones.
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-serif text-gray-800 mb-6 flex items-center gap-3">
              <Heart className="w-7 h-7 text-rose-600" />
              How I Support You
            </h2>

            <ul className="space-y-4 mb-12">
              {[
                "Guided conversations to clarify your values and wishes",
                "Education about your options and what each document means",
                "Support in communicating your decisions to loved ones",
                "Help navigating difficult conversations with family members",
                "Ensuring documents are properly completed and accessible",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-rose-500 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-rose-50 to-rose-100 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-serif text-gray-800 mb-4">
              Ready to Plan Ahead?
            </h3>
            <p className="text-gray-600 mb-6">
              Your first 30-minute consultation is complimentary.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-8 py-4 rounded-full font-medium transition-colors duration-200 shadow-lg hover:shadow-xl"
            >
              Schedule a Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default AdvanceCarePlanning;
